import { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs, EffectFade, Scrollbar, Mousewheel } from 'swiper/modules';
import {ArrowLeft, ArrowRight} from 'lucide-react';
import { Hotspot, SlideData } from '../types';
import type { Swiper as SwiperType } from 'swiper';

import 'swiper/css';
import 'swiper/css/thumbs';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';
import 'swiper/css/scrollbar';


interface MainSliderProps {
  slides: SlideData;
  onSlideChange: (activeIndex: number) => void;
  onHotspotClick: (slideIndex: number, hotspot: Hotspot, hotspotIndex: number) => void;
  onHotspotHover: (slideIndex: number, hotspotIndex: number) => void;
  onHotspotLeave: () => void;
  activeSlideIndex: number;
}

export function MainSlider({
  slides,
  onSlideChange,
  onHotspotClick,
  onHotspotHover,
  onHotspotLeave,
  activeSlideIndex
}: MainSliderProps) {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [mainSwiper, setMainSwiper] = useState<SwiperType | null>(null);

  useEffect(() => {
    if (mainSwiper && mainSwiper.activeIndex !== activeSlideIndex) {
      mainSwiper.slideTo(activeSlideIndex);
    }
  }, [activeSlideIndex, mainSwiper]);


  return (
      <div className="w-full lg:h-full flex flex-col lg:justify-between">
        <div className="lg:my-auto pt-8">
          <Swiper
            loop={true}
            autoHeight={true}
            thumbs={{ swiper: thumbsSwiper }}
            modules={[Thumbs, EffectFade]}
            onSlideChange={(swiper) => onSlideChange(swiper.activeIndex)}
            onSwiper={setMainSwiper}
            effect="fade"
            fadeEffect={{
              crossFade: true
            }}
          >
            {slides.map((slide, slideIndex) => (
              <SwiperSlide key={slideIndex} data-history={slideIndex} className="py-4 lg:py-8">
                
                <div className="absolute top-0 left-0 text-base md:text-xl lg:text-2xl font-medium tracking-tight">{slide.title}</div>
                
                <div className="relative my-8 lg:my-0 aspect-video bg-center bg-cover" style={{backgroundImage: `url(${slide.image})`}}>
                  <div className="absolute inset-0">

                    {slide.hotspots.map((hotspot, hotspotIndex) => (
                      <div
                        className='hotspot-item absolute'
                        key={hotspotIndex}
                        style={{
                          top: hotspot.coordinates.top? hotspot.coordinates.top : 'auto',
                          right: hotspot.coordinates.right? hotspot.coordinates.right : 'auto',
                          bottom: hotspot.coordinates.bottom? hotspot.coordinates.bottom : 'auto',
                          left: hotspot.coordinates.left? hotspot.coordinates.left : 'auto',
                        }}
                      >
                        
                        <button
                            className="hotspot group"
                            onClick={() => onHotspotClick(slideIndex, hotspot, hotspotIndex)}
                            onMouseEnter={() => onHotspotHover(slideIndex, hotspotIndex)}
                            onMouseLeave={onHotspotLeave}
                          >
                          <span className="hotspot-number z-10">
                          {hotspot.number}
                          </span>
                          <div className="absolute animate-ping-slower bg-tertiary w-6 h-6 md:w-7 md:h-7 rounded-full z-0"></div>
                        </button>
                        <div className={`hotspot-popover relative top-6 z-20 bg-white p-4 rounded-lg shadow-lg w-72 -translate-x-1/4 md:-translate-x-1/2 ${hotspot.coordinates.top ? (parseFloat(hotspot.coordinates.top) / 100.0 > 0.75) ? 'hotspot-popover-top' : '' : '' }`}>
                          <span className="text-sm md:text-base font-medium">
                            {hotspot.title}
                          </span>
                          <button onClick={() => onHotspotClick(slideIndex, hotspot, hotspotIndex)} className="bg-primary-light hover:bg-white border border-primary-light text-white hover:text-primary rounded-md text-sm p-4 mt-4 w-full flex justify-center items-center transition-colors duration-300">
                            Learn More
                          </button>
                        </div>
                        
                      </div>
                    ))}
                    
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="thumb-swiper-wrap">
          <div className="swiper-button-prev"><ArrowLeft size={24} strokeWidth={2} absoluteStrokeWidth={true} color={'#294F69'}/></div>
          <div className="swiper-button-next"><ArrowRight size={24} strokeWidth={2} absoluteStrokeWidth={true} color={'#294F69'}/></div>
          <Swiper
            onSwiper={setThumbsSwiper}
            spaceBetween={32}
            loop={false}
            slidesPerView={2}
            navigation={
              {
                enabled: true,
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }
            }
            scrollbar={{
              enabled: true,
              hide: false,
              draggable: true,
              dragSize: 64,
            }}
            watchSlidesProgress={true}
            modules={[Navigation, Thumbs, Scrollbar, Mousewheel]}
            className="thumbs-swiper bg-primary py-8 px-2 mx-6"
            mousewheel={true}
            breakpoints={{
              1024: {
                slidesPerView: 4,
              },
            }}
          >
            {slides.map((slide, index) => (
              <SwiperSlide key={index}>
                <div className="thumb-slide" style={{backgroundImage: `url(${slide.thumb})`}}>
                </div> 
                <div className={`text-center select-none mt-2 text-sm ${index === activeSlideIndex ? 'text-white font-semibold' : 'text-white/75'}`}>{slide.title}</div>
              </SwiperSlide>
            ))}
          </Swiper>

        </div>
      </div>
  );
}